import { dayAxis } from '@/components/charts/generic/dayAxis';
import type { MetricChartSpec } from '@/components/charts/generic/types';
import type { DomainBioscopeDay } from '@/domain/cages/overview.types';
import type { Locale } from '@/utils/localeFormatters';
import {
    getCanonicalSceneLabelTag,
    getConfiguredSceneLabelColor,
    resolveSceneLabelColor,
} from '@/utils/sceneLabelHelpers';

// ---------------------------------------------------------------------------
// Scene labels (admin only)
// ---------------------------------------------------------------------------

export interface SceneLabelsChartSpecParams {
    days: DomainBioscopeDay[];
    /** The daypart whose label shares to plot. */
    daypartTag: string;
    /** Already translated axis title. */
    axisLabel: string;
    /**
     * Display name for a **raw** scene-label tag. Pass
     * `resolveSceneLabelGraphName` from `@/helpers/sceneLabelHelpers`, which
     * renders an aliased tag as `Far away (faraway)` so two raw tags sharing a
     * category stay tellable apart in the legend.
     */
    sceneLabelLabel: (sceneLabelTag: string) => string;
    locale: Locale;
}

const SCENE_LABEL_FILL_OPACITY = 0.8;
const PERCENT_UNIT = '%';
const RATIO_TO_PERCENT = 100;

/** Category order, so bands of one category sit together in the stack. */
const SCENE_LABEL_CATEGORY_ORDER = [
    'optimal',
    'singleFish',
    'farAway',
    'tooClose',
    'blocked',
    'badAngle',
    'blurry',
];

function sceneLabelCategoryOrder(sceneLabelTag: string): number {
    const index = SCENE_LABEL_CATEGORY_ORDER.indexOf(getCanonicalSceneLabelTag(sceneLabelTag));
    // A tag this table does not know yet sorts last, where it is noticeable.
    return index === -1 ? SCENE_LABEL_CATEGORY_ORDER.length : index;
}

/**
 * Collects the scene-label tags present across the given days.
 *
 * Ordered by category, then alphabetically within it, so the stack is stable
 * whatever order the API returns and the two `tooClose` bands sit adjacent.
 */
function collectSceneLabelTags(tagsPerDay: string[][]): string[] {
    const present = new Set<string>();

    for (const tags of tagsPerDay) {
        for (const tag of tags) {
            if (tag) {
                present.add(tag);
            }
        }
    }

    return Array.from(present).sort((a, b) => {
        const byCategory = sceneLabelCategoryOrder(a) - sceneLabelCategoryOrder(b);
        return byCategory !== 0 ? byCategory : a.localeCompare(b);
    });
}

/**
 * Share of frames per scene label, stacked to 100%.
 *
 * One series per **raw** tag the days actually carry, coloured by the category it
 * belongs to and named by `resolveSceneLabelGraphName`, which appends the raw tag
 * for an aliased one — `chaos` reads "Too close (chaos)" and `tooclose` reads
 * "Too close (tooclose)", so the two bands stay tellable apart.
 *
 * Two things this fixes. The backend groups raw labels into seven categories only
 * for the response's top-level `labels[]`; `days[].sceneLabelTags` arrives
 * ungrouped, so matching against category names found nothing and the chart came
 * up empty. And the legacy mapper indexed `sceneLabelRelativeCnt` positionally
 * against a hardcoded list of category names, pairing each value with whichever
 * label happened to sit at the same index.
 *
 * The API reports shares as ratios; they are scaled to percent here.
 */
export function buildSceneLabelsChartSpec(
    params: SceneLabelsChartSpecParams
): MetricChartSpec<'line'> {
    const { days, daypartTag, axisLabel, sceneLabelLabel, locale } = params;
    const labelDays = days.filter((day) => day.daypartTag === daypartTag);

    const sceneLabelTags = collectSceneLabelTags(
        labelDays.map((day) => day.sceneLabels.map((label) => label.tag))
    );

    // Tags with no configured colour draw from the shared palette in turn, so the
    // index only advances on those — matching how the pie chart assigns them.
    let fallbackColorIndex = 0;

    return {
        kind: 'line',
        days: dayAxis(labelDays, (day) => day.day),
        series: sceneLabelTags.map((sceneLabelTag) => {
            const color = resolveSceneLabelColor(sceneLabelTag, fallbackColorIndex);
            if (!getConfiguredSceneLabelColor(sceneLabelTag)) {
                fallbackColorIndex++;
            }

            return {
                key: `sceneLabel:${sceneLabelTag}`,
                label: sceneLabelLabel(sceneLabelTag),
                color,
                fill: true,
                fillOpacity: SCENE_LABEL_FILL_OPACITY,
                data: labelDays.map((day) => {
                    const share = day.sceneLabels.find(
                        (label) => label.tag === sceneLabelTag
                    )?.relativeCount;

                    return {
                        day: day.day,
                        value: share == null ? null : share * RATIO_TO_PERCENT,
                    };
                }),
            };
        }),
        left: {
            label: axisLabel,
            unit: PERCENT_UNIT,
            stacked: true,
            min: 0,
            max: 100,
            tickDecimals: 0,
            valueDecimals: 1,
        },
        locale,
    };
}
